import React, { useState, useEffect, useMemo } from 'react';
import ShareToClientModal from './ShareToClientModal';
import ClientSharesHistory from './ClientSharesHistory';
import CommercialSharesHistory from './CommercialSharesHistory';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ShareStudyModal from './ShareStudyModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEMS_PER_PAGE = 20;

const MyStudiesPanel = ({ onSelectStudy }) => {
  const { user } = useAuth();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [favoriteFilter, setFavoriteFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showShareModal, setShowShareModal] = useState(null);
  const [showClientShareModal, setShowClientShareModal] = useState(null);
  const [showSharesHistory, setShowSharesHistory] = useState(false);
  const [showCommercialSharesHistory, setShowCommercialSharesHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchStudies();
    setCurrentPage(1); // Reset page when filters change
  }, [searchTerm, statusFilter, categoryFilter, favoriteFilter, dateFrom, dateTo]);

  const fetchStudies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (favoriteFilter) params.append('favorite', 'true');
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/my-studies?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort by date descending (most recent first)
      const sortedStudies = response.data.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });
      
      setStudies(sortedStudies);
    } catch (error) {
      console.error('Error fetching studies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(studies.length / ITEMS_PER_PAGE);
  const paginatedStudies = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return studies.slice(start, end);
  }, [studies, currentPage]);

  const updateMetadata = async (analysisId, updates) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/analysis/${analysisId}/metadata`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStudies();
    } catch (error) {
      console.error('Error updating metadata:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const toggleFavorite = async (study) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/analysis/${study.analysis_id}/toggle-personal-favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStudies();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Erreur lors de la mise à jour des favoris');
    }
  };

  const archiveStudy = async (study) => {
    const newStatus = study.status === 'archived' ? 'active' : 'archived';
    await updateMetadata(study.analysis_id, { status: newStatus });
  };

  const deleteStudy = async (analysisId, shareId = null) => {
    try {
      const token = localStorage.getItem('access_token');
      // Add share_id as query parameter if provided (for non-owners)
      const url = shareId 
        ? `${API}/analysis/${analysisId}?share_id=${shareId}`
        : `${API}/analysis/${analysisId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStudies();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting study:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
    setCategoryFilter('all');
    setFavoriteFilter(false);
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatNumber = (num) => {
    if (!num) return '-';
    return new Intl.NumberFormat('fr-FR').format(Math.round(num));
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
      {/* Header with Statistics */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{
              fontFamily: 'var(--font-titre)',
              fontWeight: '600',
              color: '#191D1E',
              fontSize: '1.5rem',
              margin: '0 0 0.375rem 0',
              letterSpacing: '-0.02em'
            }}>
              Mes Études
            </h2>
            <p style={{
              fontFamily: 'var(--font-corps)',
              color: '#687175',
              fontSize: '0.875rem',
              margin: '0'
            }}>
              Gérez et consultez toutes vos analyses multisite
            </p>
          </div>
          {/* Button to view client shares history - only for Commercial users */}
          {user?.role === 'commercial' && (
            <button
              onClick={() => setShowSharesHistory(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(57, 125, 255, 0.06)',
                color: '#397DFF',
                border: '1px solid rgba(57, 125, 255, 0.2)',
                borderRadius: '10px',
                fontSize: '0.8125rem',
                fontWeight: '500',
                fontFamily: 'var(--font-corps)',
                cursor: 'pointer',
                transition: 'all 200ms ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(57, 125, 255, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(57, 125, 255, 0.06)';
              }}
              data-testid="view-shares-history-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
              Historique des partages
            </button>
          )}
          {/* Button to view commercial shares history - only for ADV users */}
          {(user?.role === 'adv' || user?.role === 'superadmin') && (
            <button
              onClick={() => setShowCommercialSharesHistory(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'rgba(57, 125, 255, 0.06)',
                color: '#397DFF',
                border: '1px solid rgba(57, 125, 255, 0.2)',
                borderRadius: '10px',
                fontSize: '0.8125rem',
                fontWeight: '500',
                fontFamily: 'var(--font-corps)',
                cursor: 'pointer',
                transition: 'all 200ms ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(57, 125, 255, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(57, 125, 255, 0.06)';
              }}
              data-testid="view-commercial-shares-history-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              Partages aux Commerciaux
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E2E6EA',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '1.5rem'
        }}>
          {/* First Row: Search and Favorites */}
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '12px', alignItems: 'end', marginBottom: '12px' }}>
            <div>
              <label style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#3C4649',
                display: 'block',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                Rechercher
              </label>
              <input
                type="text"
                placeholder="Client, PRM, site..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #E2E6EA',
                  borderRadius: '8px',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.875rem',
                  color: '#191D1E',
                  background: '#FAFBFC',
                  outline: 'none',
                  transition: 'all 200ms ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => { e.target.style.borderColor = '#397DFF'; e.target.style.boxShadow = '0 0 0 3px rgba(57,125,255,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#E2E6EA'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div>
              <button
                onClick={() => setFavoriteFilter(!favoriteFilter)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: `1.5px solid ${favoriteFilter ? '#F9C80E' : '#E2E6EA'}`,
                  borderRadius: '8px',
                  background: favoriteFilter ? 'rgba(249, 200, 14, 0.08)' : '#FAFBFC',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 200ms ease',
                  color: favoriteFilter ? '#B8930A' : '#687175',
                  boxSizing: 'border-box'
                }}
              >
                Favoris
              </button>
            </div>
          </div>

          {/* Second Row: Date Filters + Reset Button */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#3C4649',
                display: 'block',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                Date de
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #E2E6EA',
                  borderRadius: '8px',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.875rem',
                  color: '#191D1E',
                  background: '#FAFBFC',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#3C4649',
                display: 'block',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                Date à
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #E2E6EA',
                  borderRadius: '8px',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.875rem',
                  color: '#191D1E',
                  background: '#FAFBFC',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <button
                onClick={clearFilters}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1.5px solid #E2E6EA',
                  borderRadius: '8px',
                  background: '#FFFFFF',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 200ms ease',
                  color: '#687175',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#F5F6F9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FFFFFF';
                }}
              >
                Réinitialiser les filtres
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Studies List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '64px 24px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #E2E6EA',
            borderTopColor: '#397DFF',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ fontFamily: 'var(--font-corps)', color: '#687175', fontSize: '0.875rem' }}>Chargement des études...</p>
        </div>
      ) : studies.length === 0 ? (
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E2E6EA',
          borderRadius: '12px',
          padding: '64px 24px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            background: 'rgba(57, 125, 255, 0.08)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          </div>
          <h3 style={{ margin: '0 0 6px 0', color: '#191D1E', fontFamily: 'var(--font-titre)', fontWeight: '500', fontSize: '1.0625rem' }}>
            Aucune étude trouvée
          </h3>
          <p style={{ color: '#939BA0', margin: '0', fontFamily: 'var(--font-corps)', fontSize: '0.875rem' }}>
            {searchTerm || statusFilter !== 'active' || categoryFilter !== 'all' || favoriteFilter || dateFrom || dateTo
              ? 'Modifiez les filtres pour voir plus de résultats'
              : 'Aucune étude active trouvée'}
          </p>
        </div>
      ) : (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {paginatedStudies.map((study) => (
            <div
              key={`${study.analysis_id}_${study.share_id || 'owner'}`}
              style={{
                background: '#FFFFFF',
                border: `1.5px solid ${study.favorite ? '#F9C80E' : '#E2E6EA'}`,
                borderRadius: '12px',
                padding: '20px 24px',
                boxShadow: study.favorite
                  ? '0 2px 12px rgba(249, 200, 14, 0.12)'
                  : '0 1px 3px rgba(25, 29, 30, 0.05)',
                transition: 'all 200ms ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = study.favorite
                  ? '0 4px 20px rgba(249, 200, 14, 0.2)'
                  : '0 4px 16px rgba(25, 29, 30, 0.1)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = study.favorite
                  ? '0 2px 12px rgba(249, 200, 14, 0.12)'
                  : '0 1px 3px rgba(25, 29, 30, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => onSelectStudy(study.analysis_id, study.share_id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                    <h3 
                      style={{
                        margin: 0,
                        fontFamily: 'var(--font-titre)',
                        fontSize: '1.125rem',
                        color: '#191D1E',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                        display: 'inline-block',
                        textDecoration: 'none',
                        letterSpacing: '-0.01em'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#397DFF';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#191D1E';
                      }}
                    >
                      {study.title || study.client_name}
                    </h3>
                    
                    {/* Badges */}
                    {study.status === 'archived' && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        Archivée
                      </span>
                    )}
                    
                    {/* Badge for shared studies */}
                    {study.is_shared && study.share_info && (
                      <>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          background: '#dbeafe',
                          color: 'var(--pde-azur)',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          Partagée par {study.share_info.shared_by_email}
                        </span>
                        
                        {(() => {
                          // Show "NOUVEAU" badge only if study was shared less than 48 hours ago
                          if (!study.share_info?.shared_at) return null;
                          
                          const sharedAt = new Date(study.share_info.shared_at);
                          const now = new Date();
                          const hoursDiff = (now - sharedAt) / (1000 * 60 * 60);
                          
                          return hoursDiff <= 48;
                        })() && (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            background: 'var(--pde-ambre)',
                            color: 'white',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            Nouveau
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '1rem',
                    marginBottom: '0.75rem'
                  }}>
                    <div>
                      <span style={{ fontSize: '0.6875rem', color: '#939BA0', fontFamily: 'var(--font-corps)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sites</span>
                      <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#191D1E', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-titre)' }}>
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-perimetre.png`}
                          alt="Sites" 
                          style={{ height: '1.125rem', width: 'auto' }} 
                        />
                        {study.total_sites || 0}
                      </div>
                    </div>

                    <div>
                      <span style={{ fontSize: '0.6875rem', color: '#939BA0', fontFamily: 'var(--font-corps)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Consommation</span>
                      <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#191D1E', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-titre)' }}>
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-conso.png`}
                          alt="Consommation" 
                          style={{ height: '1.125rem', width: 'auto' }} 
                        />
                        {formatNumber(study.consumption_global_mwh)} MWh
                      </div>
                    </div>

                    <div>
                      <span style={{ fontSize: '0.6875rem', color: '#939BA0', fontFamily: 'var(--font-corps)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Budget HTVA</span>
                      <div style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#191D1E', display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-titre)' }}>
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-htva.png`}
                          alt="Budget HTVA" 
                          style={{ height: '1.125rem', width: 'auto' }} 
                        />
                        {formatNumber(study.budget_actuel_htva)} €
                      </div>
                    </div>
                  </div>

                  <div style={{ fontSize: '0.8125rem', color: '#939BA0', fontFamily: 'var(--font-corps)' }}>
                    Créée le {formatDate(study.created_at)}
                    {user.role === 'superadmin' && study.user_email && (
                      <span> · {study.user_email}</span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '1rem' }}>
                  {/* First row - 3 small buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(study);
                      }}
                      style={{
                        padding: '8px 10px',
                        border: 'none',
                        borderRadius: '8px',
                        background: study.favorite ? 'rgba(249, 200, 14, 0.12)' : '#F5F6F9',
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title={study.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill={study.favorite ? '#F9C80E' : 'none'} stroke={study.favorite ? '#F9C80E' : '#939BA0'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        archiveStudy(study);
                      }}
                      style={{
                        padding: '8px 10px',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#F5F6F9',
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title={study.status === 'archived' ? 'Restaurer' : 'Archiver'}
                    >
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/picto-archiver.png`}
                        alt="Archiver" 
                        style={{ height: '1.25rem', width: 'auto' }} 
                      />
                    </button>

                    {/* Delete/Remove button - Available for all users */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(study.analysis_id);
                      }}
                      style={{
                        padding: '8px 10px',
                        border: 'none',
                          borderRadius: '8px',
                          background: '#FEF2F2',
                          color: '#EF4444',
                          cursor: 'pointer',
                          transition: 'all 200ms ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Supprimer"
                      >
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/picto-supprimer.png`}
                          alt="Supprimer" 
                          style={{ height: '1.25rem', width: 'auto' }} 
                        />
                      </button>
                  </div>
                  
                  {/* Second row - Share button (bigger and full width) */}
                  {((study.is_owner && user.role === 'adv') || user.role === 'superadmin') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowShareModal(study);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: '1.5px solid #397DFF',
                        borderRadius: '8px',
                        background: 'rgba(57, 125, 255, 0.06)',
                        color: '#397DFF',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        fontFamily: 'var(--font-corps)',
                        transition: 'all 200ms ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#397DFF';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(57, 125, 255, 0.06)';
                        e.currentTarget.style.color = '#397DFF';
                      }}
                      title="Partager avec des commerciaux"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                      <span>Partager</span>
                    </button>
                  )}
                  
                  {/* Client Share Button - For Commercial users (same style as ADV Share) */}
                  {user.role === 'commercial' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowClientShareModal(study);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: '1.5px solid #397DFF',
                        borderRadius: '8px',
                        background: 'rgba(57, 125, 255, 0.06)',
                        color: '#397DFF',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        fontFamily: 'var(--font-corps)',
                        transition: 'all 200ms ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        marginTop: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#397DFF';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(57, 125, 255, 0.06)';
                        e.currentTarget.style.color = '#397DFF';
                      }}
                      title="Partager par email avec un client"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                      <span>Partager par email</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Delete Confirmation */}
              {showDeleteConfirm === study.analysis_id && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px'
                }}>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#991b1b', fontWeight: '600' }}>
                    {study.is_owner || user.role === 'superadmin' 
                      ? 'Êtes-vous sûr de vouloir supprimer cette étude définitivement ? Cette action est irréversible.'
                      : 'Êtes-vous sûr de vouloir supprimer cette étude ? Elle restera disponible pour son créateur.'
                    }
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteStudy(study.analysis_id, study.share_id);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        border: 'none',
                        borderRadius: '6px',
                        background: '#dc2626',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Oui, supprimer
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(null);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid var(--pde-border)',
                        borderRadius: '6px',
                        background: 'white',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '6px',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #E2E6EA'
          }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 14px',
                background: currentPage === 1 ? '#F5F6F9' : '#FFFFFF',
                border: '1px solid #E2E6EA',
                borderRadius: '8px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1,
                fontFamily: 'var(--font-corps)',
                fontSize: '0.8125rem',
                color: '#3C4649',
                transition: 'all 200ms ease'
              }}
            >
              Précédent
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                style={{
                  padding: '8px 12px',
                  background: currentPage === page ? '#397DFF' : '#FFFFFF',
                  color: currentPage === page ? '#FFFFFF' : '#3C4649',
                  border: '1px solid',
                  borderColor: currentPage === page ? '#397DFF' : '#E2E6EA',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: currentPage === page ? '600' : '400',
                  minWidth: '36px',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.8125rem',
                  transition: 'all 200ms ease',
                  boxShadow: currentPage === page ? '0 2px 8px rgba(57, 125, 255, 0.2)' : 'none'
                }}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 14px',
                background: currentPage === totalPages ? '#F5F6F9' : '#FFFFFF',
                border: '1px solid #E2E6EA',
                borderRadius: '8px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.5 : 1,
                fontFamily: 'var(--font-corps)',
                fontSize: '0.8125rem',
                color: '#3C4649',
                transition: 'all 200ms ease'
              }}
            >
              Suivant
            </button>
          </div>
        )}
        </>
      )}

      <div style={{
        textAlign: 'center',
        marginTop: '2rem',
        padding: '1rem',
        color: 'var(--pde-gris-acier)',
        fontSize: '0.875rem'
      }}>
        Page {currentPage}/{totalPages || 1} • {studies.length} étude{studies.length !== 1 ? 's' : ''} au total
      </div>
      
      {/* Share Modal */}
      {showShareModal && (
        <ShareStudyModal
          study={showShareModal}
          onClose={() => setShowShareModal(null)}
          onShareSuccess={() => {
            fetchStudies();
            setShowShareModal(null);
          }}
        />
      )}
      
      {/* Client Share Modal (Commercial → Client) */}
      {showClientShareModal && (
        <ShareToClientModal
          study={showClientShareModal}
          isOpen={true}
          onClose={() => setShowClientShareModal(null)}
          onSuccess={(data) => {
            setShowClientShareModal(null);
            alert(`Email envoyé à ${data.client_email} avec succès !`);
          }}
        />
      )}
      
      {/* Client Shares History Modal */}
      <ClientSharesHistory
        isOpen={showSharesHistory}
        onClose={() => setShowSharesHistory(false)}
      />
      
      {/* Commercial Shares History Modal (ADV view) */}
      <CommercialSharesHistory
        isOpen={showCommercialSharesHistory}
        onClose={() => setShowCommercialSharesHistory(false)}
      />
    </div>
  );
};

export default MyStudiesPanel;
